import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { productionTestAPI } from '../services/api';

const FOLDER_RE = /^[a-zA-Z0-9_]+$/;

const STATUS_COLORS = {
  healthy: '#27ae60',
  degraded: '#f39c12',
  broken: '#e74c3c',
  error: '#95a5a6'
};

function StatusBadge({ status }) {
  if (!status) return <span style={{ color: '#999' }}>—</span>;
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '12px',
      fontSize: '0.78rem', fontWeight: 600,
      background: STATUS_COLORS[status] || '#95a5a6', color: 'white'
    }}>
      {status}
    </span>
  );
}

/** Groups tests array into [{folder, tests}], Uncategorized last */
function groupByFolder(tests) {
  const map = {};
  for (const t of tests) {
    const key = t.folder || '';
    if (!map[key]) map[key] = [];
    map[key].push(t);
  }
  const named = Object.keys(map)
    .filter(k => k)
    .sort((a, b) => a.localeCompare(b))
    .map(k => ({ folder: k, tests: map[k] }));
  const uncategorized = map[''] ? [{ folder: '', tests: map[''] }] : [];
  return [...named, ...uncategorized];
}

function FolderSection({ folder, tests, allFolders, onRefresh, runningIds, setRunningIds }) {
  const [open, setOpen] = useState(true);
  const [movingId, setMovingId] = useState(null);
  const [moveTarget, setMoveTarget] = useState('');
  const [newFolderInput, setNewFolderInput] = useState('');
  const [runAllLoading, setRunAllLoading] = useState(false);

  const displayName = folder || 'Uncategorized';
  const isUncategorized = !folder;

  const handleRun = async (id) => {
    setRunningIds(prev => new Set(prev).add(id));
    try {
      await productionTestAPI.run(id);
      await onRefresh();
    } catch (err) {
      alert('Run failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setRunningIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    }
  };

  const handleRunAll = async () => {
    if (!window.confirm(`Run all ${tests.length} test${tests.length !== 1 ? 's' : ''} in "${displayName}"?`)) return;
    setRunAllLoading(true);
    for (const t of tests) {
      setRunningIds(prev => new Set(prev).add(t._id));
      try { await productionTestAPI.run(t._id); } catch (_) {}
      setRunningIds(prev => { const n = new Set(prev); n.delete(t._id); return n; });
    }
    setRunAllLoading(false);
    await onRefresh();
  };

  const handleRename = async () => {
    const newName = window.prompt(`Rename folder "${displayName}" to:`, folder);
    if (newName === null) return;
    const trimmed = newName.trim();
    if (trimmed === folder) return;
    if (trimmed && !FOLDER_RE.test(trimmed)) {
      alert('Folder name can only contain letters, digits, and underscores (no spaces).');
      return;
    }
    try {
      await productionTestAPI.renameFolder(folder, trimmed);
      await onRefresh();
    } catch (err) {
      alert('Rename failed: ' + err.message);
    }
  };

  const handleMoveStart = (id) => {
    setMovingId(id);
    setMoveTarget('');
    setNewFolderInput('');
  };

  const handleMoveConfirm = async (id) => {
    const target = moveTarget === '__new__' ? newFolderInput.trim() : moveTarget;
    if (target && !FOLDER_RE.test(target)) {
      alert('Folder name can only contain letters, digits, and underscores (no spaces).');
      return;
    }
    try {
      await productionTestAPI.moveToFolder([id], target);
      setMovingId(null);
      await onRefresh();
    } catch (err) {
      alert('Move failed: ' + err.message);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its results?`)) return;
    try {
      await productionTestAPI.delete(id);
      await onRefresh();
    } catch (err) {
      alert('Delete failed: ' + err.message);
    }
  };

  const otherFolders = allFolders.filter(f => f !== folder);

  return (
    <div style={{ marginBottom: '1rem' }}>
      {/* Folder header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.6rem',
        padding: '0.6rem 0.9rem',
        background: isUncategorized ? '#f8f9fa' : '#eef4fb',
        borderRadius: open ? '6px 6px 0 0' : '6px',
        border: '1px solid ' + (isUncategorized ? '#e0e0e0' : '#c5d9ef'),
        cursor: 'pointer', userSelect: 'none'
      }}
        onClick={() => setOpen(o => !o)}
      >
        <span style={{ fontSize: '0.85rem', color: '#666', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', display: 'inline-block' }}>▶</span>
        <span style={{ fontSize: '1.15rem', marginRight: '0.1rem' }}>📁</span>
        {isUncategorized
          ? <strong style={{ fontSize: '0.98rem', color: '#888' }}>{displayName}</strong>
          : <Link to={`/production-tests/${folder}`} style={{ fontSize: '0.98rem', fontWeight: 700, color: '#1a3c5e', textDecoration: 'none' }} onClick={e => e.stopPropagation()}>{displayName}</Link>
        }
        <span style={{ fontSize: '0.82rem', color: '#777', marginLeft: '0.1rem' }}>
          ({tests.length} test{tests.length !== 1 ? 's' : ''})
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.4rem' }} onClick={e => e.stopPropagation()}>
          {!isUncategorized && (
            <button onClick={handleRename} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}>
              Rename
            </button>
          )}
          <button
            onClick={handleRunAll}
            disabled={runAllLoading}
            className="btn btn-success"
            style={{ fontSize: '0.75rem', padding: '0.2rem 0.55rem' }}
          >
            {runAllLoading ? 'Running…' : 'Run All'}
          </button>
        </div>
      </div>

      {/* Tests table */}
      {open && (
        <div style={{ border: '1px solid ' + (isUncategorized ? '#e0e0e0' : '#c5d9ef'), borderTop: 'none', borderRadius: '0 0 6px 6px', overflow: 'hidden' }}>
          <table style={{ fontSize: '0.875rem', margin: 0 }}>
            <thead>
              <tr style={{ background: '#fafafa' }}>
                <th>Name</th>
                <th>Endpoint</th>
                <th style={{ textAlign: 'center' }}>Cases</th>
                <th>Status</th>
                <th>Last Run</th>
                <th style={{ textAlign: 'center' }}>Success %</th>
                <th style={{ textAlign: 'center' }}>P95 ms</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tests.map(test => {
                const isRunning = runningIds.has(test._id);
                const lr = test.lastResult;
                const metrics = lr?.metrics;
                const isMoving = movingId === test._id;

                return (
                  <React.Fragment key={test._id}>
                    <tr>
                      <td>
                        <Link to={`/production-tests/test/${test._id}`} style={{ fontWeight: 500 }}>
                          {test.name}
                        </Link>
                        {test.description && (
                          <div style={{ fontSize: '0.78rem', color: '#777', marginTop: '0.15rem' }}>{test.description}</div>
                        )}
                      </td>
                      <td>
                        <code style={{ fontSize: '0.78rem', wordBreak: 'break-all' }}>
                          {test.endpoint.replace('https://api-marketplace.webparsers.com', '…')}
                        </code>
                      </td>
                      <td style={{ textAlign: 'center' }}>{test.test_cases?.length || 0}</td>
                      <td><StatusBadge status={lr?.status} /></td>
                      <td style={{ fontSize: '0.82rem', color: '#666', whiteSpace: 'nowrap' }}>
                        {lr ? new Date(lr.createdAt).toLocaleString() : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {metrics?.success_rate != null ? (
                          <span style={{ color: metrics.success_rate >= 90 ? '#27ae60' : '#e74c3c', fontWeight: 600 }}>
                            {metrics.success_rate}%
                          </span>
                        ) : '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        {metrics?.latency_metrics?.p95 != null ? (
                          <span style={{ color: metrics.latency_metrics.p95 > 4000 ? '#f39c12' : 'inherit' }}>
                            {metrics.latency_metrics.p95}
                          </span>
                        ) : '—'}
                      </td>
                      <td>
                        <div className="flex" style={{ gap: '0.35rem', flexWrap: 'wrap' }}>
                          <button onClick={() => handleRun(test._id)} disabled={isRunning} className="btn btn-success" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                            {isRunning ? '…' : 'Run'}
                          </button>
                          <Link to={`/production-tests/test/${test._id}`} className="btn btn-primary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                            View
                          </Link>
                          <Link to={`/production-tests/test/${test._id}/edit`} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                            Edit
                          </Link>
                          <button onClick={() => isMoving ? setMovingId(null) : handleMoveStart(test._id)} className="btn btn-secondary" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                            📁
                          </button>
                          <button onClick={() => handleDelete(test._id, test.name)} className="btn btn-danger" style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem' }}>
                            ✕
                          </button>
                        </div>
                      </td>
                    </tr>

                    {/* Inline move row */}
                    {isMoving && (
                      <tr style={{ background: '#fffbea' }}>
                        <td colSpan={8} style={{ padding: '0.5rem 1rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: 500 }}>Move to folder:</span>
                            <select
                              value={moveTarget}
                              onChange={e => setMoveTarget(e.target.value)}
                              style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem' }}
                            >
                              <option value="">— Uncategorized —</option>
                              {otherFolders.map(f => <option key={f} value={f}>{f}</option>)}
                              <option value="__new__">+ New folder…</option>
                            </select>
                            {moveTarget === '__new__' && (
                              <input
                                type="text"
                                placeholder="New folder name"
                                value={newFolderInput}
                                onChange={e => setNewFolderInput(e.target.value)}
                                style={{ padding: '0.3rem 0.5rem', fontSize: '0.85rem', width: '180px', border: '1px solid #ccc', borderRadius: '4px' }}
                                autoFocus
                              />
                            )}
                            <button
                              onClick={() => handleMoveConfirm(test._id)}
                              disabled={moveTarget === '__new__' && !newFolderInput.trim()}
                              className="btn btn-primary"
                              style={{ fontSize: '0.82rem', padding: '0.3rem 0.7rem' }}
                            >
                              Move
                            </button>
                            <button onClick={() => setMovingId(null)} className="btn btn-secondary" style={{ fontSize: '0.82rem', padding: '0.3rem 0.7rem' }}>
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProductionTestsList() {
  const { folder: folderFilter } = useParams();
  const [tests, setTests] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [runningIds, setRunningIds] = useState(new Set());
  const [error, setError] = useState(null);

  const fetchTests = useCallback(async () => {
    try {
      const [testsRes, foldersRes] = await Promise.all([
        productionTestAPI.getAll(),
        productionTestAPI.getFolders()
      ]);
      setTests(testsRes.data);
      setAllFolders(foldersRes.data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTests(); }, [fetchTests]);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  const filteredTests = folderFilter ? tests.filter(t => (t.folder || '') === folderFilter) : tests;
  const groups = groupByFolder(filteredTests);

  return (
    <div>
      <div className="card">
        <div className="flex-between mb-3">
          <div>
            {folderFilter
              ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <Link to="/production-tests" style={{ fontSize: '0.88rem', color: '#666', textDecoration: 'none' }}>← All Tests</Link>
                  <h2 style={{ margin: 0 }}>📁 {folderFilter}</h2>
                  <span style={{ fontSize: '0.85rem', color: '#888' }}>({filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''})</span>
                </div>
              )
              : <h2>Production Tests</h2>
            }
          </div>
          <Link to="/production-tests/create" className="btn btn-primary">+ New Test</Link>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {tests.length === 0 ? (
          <div className="alert alert-info">
            No production tests yet. <Link to="/production-tests/create">Create one</Link> to get started.
          </div>
        ) : (
          groups.map(g => (
            <FolderSection
              key={g.folder}
              folder={g.folder}
              tests={g.tests}
              allFolders={allFolders}
              onRefresh={fetchTests}
              runningIds={runningIds}
              setRunningIds={setRunningIds}
            />
          ))
        )}
      </div>
    </div>
  );
}

export default ProductionTestsList;
